import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchOffIcon from '@mui/icons-material/SearchOff';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { getShortDescription } from '~/Nova/scenes/HostingList/utils/helpers';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';
import EmptySectionCard from '~/Nova/scenes/Site/components/EmptySectionCard';

import SelectableListCard from '../../../components/SelectableListCard';
import SelectableListCardLoading from '../../../components/SelectableListCardLoading';
import { getImageLinks, getLogoConfig } from '../../../utils/imageLogos';
import ReImageOSModal from './components/ReImageOSModal';

interface ReImageServerOSProps {
  osFilteredList: Record<string, PublicImageItem[]>;
  hostingId: string;
  osCurrentImage: PublicImageItem | undefined | any;
  isLoadingOSImages: boolean;
  isSelfManagedDedi?: boolean;
  searchQuery?: string;
}

const ReImageServerOS = ({
  osFilteredList,
  hostingId,
  osCurrentImage,
  isLoadingOSImages,
  isSelfManagedDedi = false,
  searchQuery = '',
}: ReImageServerOSProps) => {
  const { t } = useTranslation('selfManagedHosting');
  const [openSelfManagedOSModal, setOpenSelfManagedOSModal] = useState(false);
  const [selectedOS, setSelectedOS] = useState('');
  const { os_name: currentOSName } = osCurrentImage || {};
  const osImagesByNamesList = Object.keys(osFilteredList);

  const handleSelectedOS = (selectedOS: string) => {
    setOpenSelfManagedOSModal(true);
    setSelectedOS(selectedOS);
  };

  // Early return if osFilteredList is empty (parent component handles alert and navigation)
  if (!osFilteredList || Object.keys(osFilteredList).length === 0) {
    if (isLoadingOSImages) {
      return <SelectableListCardLoading />;
    }

    return searchQuery ? (
      <EmptySectionCard>
        <Stack alignItems="center" spacing={2}>
          <IconButton disableRipple>
            <SearchOffIcon sx={{ fontSize: 56, color: '#244b9c' }} />
          </IconButton>

          <Typography variant="h3">
            {t('reimageSearch.emptyState.title')}
          </Typography>

          <Typography variant="body1">
            {t('reimageSearch.emptyState.description')}
          </Typography>
        </Stack>
      </EmptySectionCard>
    ) : null;
  }

  return (
    <>
      <Stack spacing={2}>
        {!isLoadingOSImages &&
          osImagesByNamesList.map((osName) => {
            const osMeta = osFilteredList[osName]?.[0];
            const { logo: osLogo } = getLogoConfig(osName);
            const { forumUrl } = getImageLinks(osMeta?.links);
            const isCurrentImage = currentOSName === osName;
            const shortDescription = getShortDescription(
              osMeta?.description ?? '',
            );
            return (
              <SelectableListCard
                key={osName}
                app={osName}
                title={osMeta?.os_name ?? osName}
                description={osMeta?.description ?? ''}
                shortDescription={shortDescription ?? ''}
                icon={osLogo}
                buttonText={t('selectBtn')}
                onSelect={handleSelectedOS}
                testId={`self-managed-card-${osName}`}
                forumUrl={forumUrl}
                showCurrentServerImageOrApp={isCurrentImage}
              />
            );
          })}
        {isLoadingOSImages && <SelectableListCardLoading />}
      </Stack>
      <ReImageOSModal
        open={openSelfManagedOSModal}
        setOpen={setOpenSelfManagedOSModal}
        selectedOS={selectedOS}
        osFilteredList={osFilteredList}
        hostingId={hostingId}
        isSelfManagedDedi={isSelfManagedDedi}
      />
    </>
  );
};

export default ReImageServerOS;
